import { useState, useEffect } from "react";
import { Trash, Pencil, Plus, RefreshCw, X, AlertCircle } from "lucide-react";

export default function CategoriesManagement() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newCategory, setNewCategory] = useState({ title: "" });
  const [editingCategory, setEditingCategory] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentView, setCurrentView] = useState("all"); // all, active, inactive

  const baseURL = process.env.REACT_APP_API_BASE_URL;

  // Get token from session storage
  const getToken = () => {
    return sessionStorage.getItem("token");
  };

  // Headers with authorization
  const getHeaders = () => {
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    };
  };

  // Fetch all categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${baseURL}/api/categories`, {
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch categories");
      }

      const data = await response.json();
      setCategories(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Add new category
  const addCategory = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${baseURL}/api/categories`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(newCategory),
      });

      if (!response.ok) {
        throw new Error("Failed to add category");
      }

      await fetchCategories();
      setNewCategory({ title: "" });
      setShowAddModal(false);
    } catch (err) {
      setError(err.message);
    }
  };

  // Update category
  const updateCategory = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `${baseURL}/api/categories/${editingCategory.categoryId}`,
        {
          method: "PUT",
          headers: getHeaders(),
          body: JSON.stringify(editingCategory),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update category");
      }

      await fetchCategories();
      setEditingCategory(null);
      setShowEditModal(false);
    } catch (err) {
      setError(err.message);
    }
  };

  // Delete category
  const deleteCategory = async (id) => {
    try {
      const response = await fetch(`${baseURL}/api/categories/${id}?status=0`, {
        method: "DELETE",
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error("Failed to delete category");
      }

      await fetchCategories();
    } catch (err) {
      setError(err.message);
    }
  };

  // Restore category
  const restoreCategory = async (id) => {
    try {
      const response = await fetch(`${baseURL}/api/categories/${id}?status=1`, {
        method: "DELETE",
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error("Failed to restore category");
      }

      await fetchCategories();
    } catch (err) {
      setError(err.message);
    }
  };

  // Load categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Handle form changes for new category
  const handleNewCategoryChange = (e) => {
    const { name, value } = e.target;
    setNewCategory((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form changes for editing category
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditingCategory((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Start editing a category
  const startEdit = (category) => {
    setEditingCategory({ ...category });
    setShowEditModal(true);
  };

  // Filter categories based on search term and current view
  const filteredCategories = categories.filter((category) => {
    const matchesSearch = category.title
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    if (currentView === "all") return matchesSearch;
    if (currentView === "active") return matchesSearch && category.status === 1;
    if (currentView === "inactive")
      return matchesSearch && category.status === 0;
    return matchesSearch;
  });

  // Modal component
  const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md animate-fadeIn">
          <div className="flex justify-between items-center border-b px-6 py-4 bg-gray-800 text-white rounded-t-lg">
            <h3 className="text-lg font-semibold">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-300 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          <div className="px-6 py-6">{children}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-0 sm:px-4 py-8">
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
        <div className="p-6 border-b">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <h1 className="text-2xl font-bold text-_dark flex gap-3">
              Categories <span className="hidden sm:block">Management</span> 
            </h1>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center bg-_blue hover:bg-_dark text-white px-4 py-2 rounded transition-colors"
            >
              <Plus className="mr-1" size={16} />
              Add Category
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search input */}
            <div className="relative flex-grow">
              <input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border border-gray-300 rounded-lg w-full py-2 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Filter tabs */}
            <div className="hidden sm:block flex rounded-lg overflow-hidden border border-gray-300">
              <button
                onClick={() => setCurrentView("all")}
                className={`px-4 py-2 text-sm ${
                  currentView === "all"
                    ? "bg-_blue border text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                All
              </button>
              <button
                onClick={() => setCurrentView("active")}
                className={`px-4 py-2 text-sm border-l border-gray-300 ${
                  currentView === "active"
                    ? "bg-_blue border text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Active
              </button>
              <button
                onClick={() => setCurrentView("inactive")}
                className={`px-4 py-2 text-sm border-l border-gray-300 ${
                  currentView === "inactive"
                    ? "bg-_blue border text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Inactive
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mx-6 my-4 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded flex items-start">
            <AlertCircle className="mr-2 flex-shrink-0" size={20} />
            <p>{error}</p>
          </div>
        )}

        {/* Categories List */}
        <div className="overflow-x-auto hidden sm:block">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredCategories.length === 0 ? (
                <tr>
                  <td
                    colSpan="4"
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No categories found
                  </td>
                </tr>
              ) : (
                filteredCategories.map((category) => (
                  <tr
                    key={category.categoryId}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-gray-800">
                      {category.categoryId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-800 font-medium">
                      {category.title}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          category.status === 1
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {category.status === 1 ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => startEdit(category)}
                          className="text-blue-600 hover:text-blue-800 p-1 transition-colors rounded-full hover:bg-blue-100"
                          title="Edit"
                        >
                          <Pencil size={18} />
                        </button>
                        {category.status === 1 ? (
                          <button
                            onClick={() => deleteCategory(category.categoryId)}
                            className="text-red-600 hover:text-red-800 p-1 transition-colors rounded-full hover:bg-red-100"
                            title="Deactivate"
                          >
                            <Trash size={18} />
                          </button>
                        ) : (
                          <button
                            onClick={() => restoreCategory(category.categoryId)}
                            className="text-green-600 hover:text-green-800 p-1 transition-colors rounded-full hover:bg-green-100"
                            title="Activate"
                          >
                            <RefreshCw size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile view cards (visible only on small screens) */}
        <div className="sm:hidden">
          {!loading && filteredCategories.length > 0 && (
            <div className="divide-y divide-gray-200">
              {filteredCategories.map((category) => (
                <div key={category.categoryId} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-medium text-gray-800">
                        {category.title}
                      </h3>
                      <p className="text-sm text-gray-500">
                        ID: {category.categoryId}
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1 text-xs leading-5 font-semibold rounded-full ${
                        category.status === 1
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {category.status === 1 ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="flex space-x-2 mt-2">
                    <button
                      onClick={() => startEdit(category)}
                      className="flex-1 flex justify-center items-center text-blue-600 border border-blue-600 px-3 py-1 rounded text-sm"
                    >
                      <Pencil size={14} className="mr-1" />
                      Edit
                    </button>
                    {category.status === 1 ? (
                      <button
                        onClick={() => deleteCategory(category.categoryId)}
                        className="flex-1 flex justify-center items-center text-red-600 border border-red-600 px-3 py-1 rounded text-sm"
                      >
                        <Trash size={14} className="mr-1" />
                        Deactivate
                      </button>
                    ) : (
                      <button
                        onClick={() => restoreCategory(category.categoryId)}
                        className="flex-1 flex justify-center items-center text-green-600 border border-green-600 px-3 py-1 rounded text-sm"
                      >
                        <RefreshCw size={14} className="mr-1" />
                        Activate
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add New Category Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New Category"
      >
        <form onSubmit={addCategory}>
          <div className="mb-4">
            <label
              htmlFor="new-title"
              className="block text-gray-700 text-sm font-medium mb-2"
            >
              Category Name
            </label>
            <input
              id="new-title"
              type="text"
              name="title"
              value={newCategory.title}
              onChange={handleNewCategoryChange}
              className="border border-gray-300 rounded-lg w-full py-2 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
              required
              placeholder="Enter category name"
            />
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={() => setShowAddModal(false)}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-_blue hover:bg-_bg_lite text-white px-4 py-2 rounded transition-colors"
            >
              Save Category
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Category Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Category"
      >
        {editingCategory && (
          <form onSubmit={updateCategory}>
            <div className="mb-4">
              <label
                htmlFor="edit-title"
                className="block text-gray-700 text-sm font-medium mb-2"
              >
                Category Name
              </label>
              <input
                id="edit-title"
                type="text"
                name="title"
                value={editingCategory.title}
                onChange={handleEditChange}
                className="border border-gray-300 rounded-lg w-full py-2 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
                required
              />
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-_blue hover:bg-_bg_lite text-white px-4 py-2 rounded transition-colors"
              >
                Update Category
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
